// @flow
import React, { Component, type Element } from "react";
import { noop, isEqual, filter, get } from "lodash";
import type { BinaryFn, UnaryFn, TernaryFn } from "@tvg/types/Functional";
import type {
  RaceCardBettingInterest,
  RaceTypeCodeEnum,
  RaceCardRunner
} from "@tvg/types/Race";
import type { RacePanelLink } from "@fdr/types/Race";
import type { UserOptedInPromos } from "@tvg/types/User";
import RunnerLine from "../../_molecules/TopRacesRunnerLine";
import Container from "./styled-components";

export type Selections = string[][];

type Props = {
  runners: RaceCardBettingInterest[],
  /**
   * Leg number for the race
   */
  leg: number,
  /**
   * Flag to activate the leg layout
   */
  isLeg: boolean,
  /**
   * Flag that controls if the race is wagerable or not
   */
  isWagerable: boolean,
  /**
   * Race type code
   */
  raceTypeCode: RaceTypeCodeEnum,
  /**
   * Array with the current selections of the RaceCard
   */
  selections: Selections,
  /**
   * Number of selection columns
   */
  numColumns: number,
  /**
   * Callback to run when user selects a runner
   */
  onRunnerClick: TernaryFn<
    RacePanelLink,
    string,
    string | UserOptedInPromos,
    void
  >,
  /**
   Reveals if the user may see the inLinePastPerformance or be redirected to the race card on the respective runnerLines
  */
  redirectToRaceCard: boolean,
  /**
   The race url to be redirected to
  */
  raceUrl: string,
  /**
   * The Race this runner list is on
   */
  race: ?RacePanelLink,
  /**
   * hover effect on runner bio
   *
   */
  hasHover: boolean,

  onSelectAllRunnerEntries: UnaryFn<string, void>,
  isLogged: boolean,
  expandedInfo: BinaryFn<number, number, Element<*> | void>,
  changeUpdate: TernaryFn<string, number, boolean, void>,
  selectedRunners: string[][],
  isOpeningApp: boolean,
  showSilks: {
    [key: string]: boolean
  }
};

const getRunnerSelections = (biNumber: number, selections: Selections) =>
  selections.map(
    (column) => (column ? column.indexOf(biNumber.toString()) : -1) >= 0
  );

class RunnerList extends Component<Props> {
  static defaultProps = {
    runners: [],
    leg: 0,
    isLeg: false,
    isWagerable: true,
    onRunnerClick: noop,
    raceTypeCode: "T",
    selections: [[]],
    numColumns: 1,
    redirectToRaceCard: false,
    raceUrl: "/",
    race: null,
    hasHover: false,
    onSelectAllRunnerEntries: noop,
    isLogged: false,
    expandedInfo: noop,
    changeUpdate: noop,
    selectedRunners: [[]],
    isOpeningApp: false,
    showSilks: {}
  };

  shouldComponentUpdate(nextProps: Props) {
    return !isEqual(nextProps, this.props);
  }

  isRunnerOpen = (runner: RaceCardRunner) => {
    if (this.props.selectedRunners[this.props.leg]) {
      return !!this.props.selectedRunners[this.props.leg].find(
        (item) => item === runner.runnerId
      );
    }
    return false;
  };

  renderRunners() {
    const flatBi = this.props.runners.reduce(
      (acc, bi: RaceCardBettingInterest) => {
        const runners = bi.runners
          ? bi.runners.map(
              (
                runner: RaceCardRunner
              ): {
                bi: RaceCardBettingInterest,
                runner: RaceCardRunner
              } => ({ bi, runner })
            )
          : [];
        return acc.concat(runners);
      },
      []
    );

    return flatBi
      .sort((a, b) =>
        a.runner.scratched
          ? a.bi.biNumber - (b.runner.scratched ? b.bi.biNumber : 0)
          : 0
      )
      .map(({ bi, runner }, index) => {
        const isCoupled =
          filter(
            this.props.runners,
            (biRunner) => biRunner.biNumber === bi.biNumber
          ).length >= 2;
        return (
          <RunnerLine
            isOpeningApp={this.props.isOpeningApp}
            isOpen={this.isRunnerOpen(runner)}
            changeUpdate={this.props.changeUpdate}
            expandedInfo={this.props.expandedInfo}
            isLogged={this.props.isLogged}
            race={this.props.race}
            runner={runner}
            bettingInterest={bi}
            isCoupled={isCoupled}
            key={`runner-${runner.runnerId}`}
            selections={getRunnerSelections(bi.biNumber, this.props.selections)}
            raceTypeCode={this.props.raceTypeCode}
            isLeg={this.props.isLeg}
            leg={this.props.leg}
            isWagerable={this.props.isWagerable}
            onRunnerClick={this.props.onRunnerClick}
            numColumns={this.props.numColumns}
            redirectToRaceCard={this.props.redirectToRaceCard}
            raceUrl={this.props.raceUrl}
            hasHover={this.props.hasHover}
            onSelectAllRunnerEntries={this.props.onSelectAllRunnerEntries}
            isNotLastRunner={index < 2}
            showSilks={
              this.props.showSilks[get(this.props, "race.tvgRaceId", "0")]
            }
          />
        );
      });
  }

  render() {
    return <Container>{this.renderRunners()}</Container>;
  }
}

export default RunnerList;
